import type { ErrorResponse } from './types';

export class ApiError extends Error {
	readonly code: string;
	readonly status: number;
	readonly data?: unknown;

	constructor(status: number, body: ErrorResponse) {
		super(body.message);
		this.name = 'ApiError';
		this.code = body.code;
		this.status = status;
		this.data = body.data;
	}
}

let onAuthLost: (() => void) | null = null;

/** 셸이 앱 시작 시 1회 호출 — 인증 상실(refresh 만료) 콜백 등록 */
export function configureAuth(opts: { onAuthLost: () => void }): void {
	onAuthLost = opts.onAuthLost;
}

let reissueInFlight: Promise<boolean> | null = null;

/** single-flight — 동시 다발 401 이 reissue 를 한 번만 호출하도록 Promise 공유 */
function reissueOnce(): Promise<boolean> {
	if (!reissueInFlight) {
		reissueInFlight = fetch('/auth/reissue', { method: 'POST', credentials: 'include' })
			.then((res) => res.ok)
			.catch(() => false)
			.finally(() => {
				reissueInFlight = null;
			});
	}
	return reissueInFlight;
}

// 자기 자신의 401 로 reissue 루프를 만들지 않는 경로
const AUTH_RETRY_EXEMPT = ['/auth/login', '/auth/reissue'];

async function parseErrorBody(res: Response): Promise<ErrorResponse> {
	try {
		return (await res.json()) as ErrorResponse;
	} catch {
		return { code: 'FE_NETWORK', message: res.statusText || `HTTP ${res.status}` };
	}
}

async function request<T>(path: string, init: RequestInit | undefined, retried: boolean): Promise<T> {
	const res = await fetch(path, {
		...init,
		credentials: 'include',
		headers: { 'Content-Type': 'application/json', ...(init?.headers as Record<string, string> | undefined) },
	});
	if (res.ok) {
		return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
	}
	const body = await parseErrorBody(res);
	const exempt = AUTH_RETRY_EXEMPT.some((p) => path.startsWith(p));
	if (res.status === 401 && !exempt && !retried) {
		if (await reissueOnce()) {
			return request<T>(path, init, true);
		}
		onAuthLost?.();
	}
	throw new ApiError(res.status, body);
}

/** 게이트웨이(same-origin) API 호출 — 쿠키 동반·에러 정규화·401 자동 reissue 1회 */
export function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
	return request<T>(path, init, false);
}
